import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiUrls } from '../shared/apiurls';

export const LOCAL_STORAGE_KEYS = {
  LOGIN: 'EZYEMPLOYEE',
  USERSCOMPANIES: 'UserCompanies',
  CHECK_PERMISSIONS: 'checkPermissions',
  LAST_ACTION: 'lastAction',
  FIRST_LOGIN: 'firstlogin',
  SELECTED_COMPANY: 'selectedCompany',
  // The property chosen in the footer. Its own key: SELECTED_COMPANY already
  // holds a product elsewhere, and one key cannot mean two things.
  SELECTED_MARSHA: 'selectedMarsha',
  COMPANY: 'company',
  ALLOW_CHANGE_PASSWORD: 'allowChangePassword',
} as const;

const CHECK_INTERVAL = 5; // in ms
// const STORE_KEY = 'lastAction';

export type LocalStorageKey =
  (typeof LOCAL_STORAGE_KEYS)[keyof typeof LOCAL_STORAGE_KEYS];

@Injectable({ providedIn: 'root' })
export class LocalStorageItemsService {
  timeoutInterval: any = null;
  static clearAll() {
    throw new Error('Method not implemented.');
  }

  companies_list: any;
  minutes_until_auto_logout: any = 30;

  public setLastAction(lastAction: number) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.LAST_ACTION, lastAction.toString());
  }
  public getLastAction() {
    return parseInt(
      localStorage.getItem(LOCAL_STORAGE_KEYS.LAST_ACTION) || 'lasAction',
    );
  }
  companyInfo: any;

  private subjects: Record<LocalStorageKey, BehaviorSubject<any | null>> =
    {} as any;

  constructor(
    private router: Router,
    private http: HttpClient,
  ) {
    this.timeoutInterval = null;
    Object.values(LOCAL_STORAGE_KEYS).forEach((key) => {
      const data = this.getFromLS(key);
      this.subjects[key] = new BehaviorSubject<any | null>(data);
    });
    this.getItem$('UserCompanies').subscribe((res) => {
      if (res?.length) {
        this.companies_list = res;
        this.minutes_until_auto_logout =
          this.companies_list[0]?.minutes_until_auto_logout;
      }
    });
  }

  private getFromLS(key: LocalStorageKey): any | null {
    try {
      return JSON.parse(localStorage.getItem(key) || 'null');
    } catch {
      return null;
    }
  }

  setItem(key: LocalStorageKey, value: any): void {
    const oldValue = this.getFromLS(key);

    // Only update if value changed
    if (JSON.stringify(oldValue) === JSON.stringify(value)) return;

    localStorage.setItem(key, JSON.stringify(value));
    this.subjects[key].next(value);
  }

  getItem$(key: LocalStorageKey): Observable<any | null> {
    return this.subjects[key].asObservable();
  }

  removeItem(key: LocalStorageKey): void {
    const oldValue = this.getFromLS(key);

    if (oldValue !== null) {
      localStorage.removeItem(key);
      this.subjects[key].next(null);
    }
  }
  clearAll(): void {
    Object.values(LOCAL_STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
      this.subjects[key].next(null);
    });
  }

  logout() {
    const cleanup = () => {
      this.clearAll();
      sessionStorage.clear();
      ['sid', 'user_id', 'full_name', 'system_user'].forEach(
        (n) =>
          (document.cookie = `${n}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/;`),
      );
      this.router
        .navigateByUrl('/', { replaceUrl: true })
        .then((ok) => {
          if (!ok) window.location.href = '/';
        })
        .catch(() => (window.location.href = '/'));
      localStorage.setItem('logout-event', Date.now().toString());
      if (this.timeoutInterval) {
        clearInterval(this.timeoutInterval);
      }
    };

    this.http.get(ApiUrls.logout).subscribe({ next: cleanup, error: cleanup });
  }

  startTimerToCheckTimeOut() {
    if (window.location.host !== '/') {
      this.check();
      this.initListener();
      this.initInterval();
      localStorage.setItem(
        LOCAL_STORAGE_KEYS.LAST_ACTION,
        Date.now().toString(),
      );
    }
  }

  initListener() {
    const resetHandler = () => this.reset();

    const events = [
      'click',
      'mousemove',
      'keydown',
      'keyup',
      'keypress',
      'scroll',
      'touchstart',
    ];

    events.forEach((event) => {
      document.body.addEventListener(event, resetHandler);
    });
  }

  reset() {
    const now = Date.now();
    this.setLastAction(now);
    localStorage.setItem(LOCAL_STORAGE_KEYS.LAST_ACTION, now.toString());
  }

  initInterval() {
    this.timeoutInterval = setInterval(() => {
      this.check();
    }, CHECK_INTERVAL);
  }

  check() {
    const lastAction = parseInt(
      localStorage.getItem(LOCAL_STORAGE_KEYS.LAST_ACTION) ??
        Date.now().toString(),
      10,
    );
    const now = Date.now();
    const timeoutMillis = this.minutes_until_auto_logout * 60 * 1000;
    const diff = lastAction + timeoutMillis - now;
    if (diff <= 0) {
      this.logout();
    }
  }
}
